import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatDate } from '@/lib/utils';
import { MonitoringLog } from '../types/monitoring';

interface LogViewerProps {
  logs: MonitoringLog[];
}

const levelConfig = {
  info: { label: 'Info', variant: 'secondary' as const },
  warn: { label: 'Warn', variant: 'warning' as const },
  error: { label: 'Error', variant: 'destructive' as const },
};

export default function LogViewer({ logs }: LogViewerProps) {
  const [level, setLevel] = useState<'all' | 'info' | 'warn' | 'error'>('all');
  const [query, setQuery] = useState('');

  const filteredLogs = useMemo(() => {
    const search = query.trim().toLowerCase();
    return logs.filter((log) => {
      const matchesLevel = level === 'all' || log.level === level;
      if (!search) {
        return matchesLevel;
      }
      const haystack = `${log.message} ${log.pipeline ?? ''}`.toLowerCase();
      return matchesLevel && haystack.includes(search);
    });
  }, [logs, level, query]);

  return (
    <div className="space-y-3 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search logs..."
            className="pl-9"
          />
        </div>
        <Select value={level} onValueChange={(value) => setLevel(value as any)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All levels</SelectItem>
            <SelectItem value="info">Info</SelectItem>
            <SelectItem value="warn">Warn</SelectItem>
            <SelectItem value="error">Error</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        {filteredLogs.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            No logs match the current filters.
          </div>
        ) : (
          filteredLogs.map((log) => {
            const config = levelConfig[log.level];
            return (
              <div key={log.id} className="rounded-md border px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant={config.variant}>{config.label}</Badge>
                  {log.pipeline && <span>{log.pipeline}</span>}
                  <span>{formatDate(log.timestamp)}</span>
                </div>
                <p className="mt-2">{log.message}</p>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
